import { PencilIcon } from "@heroicons/react/outline";
import { OpenInNewWindowIcon } from "@radix-ui/react-icons";
import { AlertCircleIcon, PlusIcon } from "lucide-react";

import { Button } from "../ui/button";
import { DialogTrigger } from "../ui/dialog";
import LabelBadge from "../ui/LabelBadge";
import { Skeleton } from "../ui/skeleton";
import { BasicTooltip } from "../ui/Tooltip";
import { OverflowDisplayTooltip } from "../ui/Tooltip/OverflowDisplayTooltip";
import { getEntityName, getItemColor } from "../UnifiedRegistry/utils";

import MiniEntityItem from "./MiniEntityItem";
import type { ChooseEntityDialogTriggerSize } from "./types";

import { EntityActionProps } from ".";

import { EntityIcon } from "@/components/ui/icons";
import { Entity, EntityType } from "@/queries";

const SIZE_CONFIG = {
  xs: {
    container: "h-9 p-1.5",
    icon: "h-5 w-5",
    iconRounded: "rounded",
    text: "text-xs",
    actionButton: "h-5 w-5",
    actionIcon: "h-3 w-3",
  },
  sm: {
    container: "h-10 p-1.5",
    icon: "h-6 w-6",
    iconRounded: "rounded-md",
    text: "text-xs",
    actionButton: "h-5 w-5",
    actionIcon: "h-3 w-3",
  },
  md: {
    container: "h-[46px] p-2",
    icon: "h-7 w-7",
    iconRounded: "rounded-md",
    text: "text-sm",
    actionButton: "h-6 w-6",
    actionIcon: "h-3.5 w-3.5",
  },
  lg: {
    container: "h-[52px] p-2.5",
    icon: "h-8 w-8",
    iconRounded: "rounded-md",
    text: "text-sm",
    actionButton: "h-7 w-7",
    actionIcon: "h-4 w-4",
  },
};

interface SingleModeTriggerProps {
  mode?: "single";
  selectedEntity?: Entity;
  selectedEntities?: never;
}

interface MultiModeTriggerProps {
  mode: "multi";
  selectedEntity?: never;
  selectedEntities?: Entity[];
  onSelect?: (entities: Entity[]) => void;
}

type EntityWithSelectedSheet = Entity & {
  selected_sheet_title?: string;
};

const LoadingState = () => (
  <>
    <Skeleton width="1.75rem" height="1.75rem" borderRadius="0.375rem" />
    <Skeleton width="120px" height="1rem" />
  </>
);

type ChooseEntityDialogTriggerProps = (SingleModeTriggerProps | MultiModeTriggerProps) &
  EntityActionProps & {
    type: EntityType;
    size?: ChooseEntityDialogTriggerSize;
    readonly?: boolean;
    isLoading?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    className?: string;
    /** Trailing badge after the entity name. Only rendered in single-selection state. */
    trailingBadge?: React.ReactNode;
  };

const getEntityIconSize = (size: ChooseEntityDialogTriggerSize): "xs" | "sm" | "md" | "lg" => {
  if (size === "lg") {
    return "md";
  }
  return "sm";
};

const ChooseEntityDialogTrigger = ({
  type,
  mode = "single",
  size = "md",
  selectedEntity,
  selectedEntities,
  onInlineCreate,
  onInlineEdit,
  url,
  className,
  readonly = false,
  isLoading = false,
  hasError = false,
  errorMessage,
  trailingBadge,
  ...props
}: ChooseEntityDialogTriggerProps) => {
  const onSelect = "onSelect" in props ? props.onSelect : undefined;
  const isMultiMode = mode === "multi";
  const hasSelection = isMultiMode ? (selectedEntities?.length ?? 0) > 0 : !!selectedEntity;
  const sizeConfig = SIZE_CONFIG[size];

  const renderSingleSelection = () => {
    if (!selectedEntity) {
      return null;
    }

    const selectedSheetTitle = (selectedEntity as EntityWithSelectedSheet).selected_sheet_title;
    const hasVersionOrLabel = selectedEntity?.version_number || selectedEntity?.label;
    const titleContent = selectedSheetTitle
      ? `${selectedEntity.name} • ${selectedSheetTitle}`
      : selectedEntity.name;

    return (
      <>
        <div
          className={`flex flex-shrink-0 items-center justify-center ${sizeConfig.icon} ${sizeConfig.iconRounded} ${getItemColor(
            selectedEntity.type,
          )}`}
        >
          <EntityIcon type={selectedEntity.type} size={getEntityIconSize(size)} />
        </div>
        <div className="flex w-0 min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <div
            className={`flex w-0 min-w-0 flex-1 items-center gap-1 overflow-hidden leading-tight ${sizeConfig.text}`}
          >
            <OverflowDisplayTooltip
              content={titleContent}
              className="min-w-0 flex-shrink truncate font-medium"
            >
              {selectedEntity.name}
            </OverflowDisplayTooltip>
            {selectedSheetTitle && (
              <>
                <span className="flex-shrink-0 text-gray-400">•</span>
                <OverflowDisplayTooltip
                  content={selectedSheetTitle}
                  className="min-w-0 flex-1 truncate text-gray-500"
                >
                  {selectedSheetTitle}
                </OverflowDisplayTooltip>
              </>
            )}
          </div>
          {hasVersionOrLabel && (
            <div className="flex flex-shrink-0 items-center gap-1.5 text-xs text-gray-500">
              {selectedEntity?.version_number && (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">
                  Version {selectedEntity.version_number}
                </span>
              )}
              {selectedEntity?.label && (
                <LabelBadge label={selectedEntity.label} variant="environment" />
              )}
            </div>
          )}
          {trailingBadge && <div className="flex flex-shrink-0 items-center">{trailingBadge}</div>}
        </div>
      </>
    );
  };

  const renderMultiSelection = () => {
    if (!selectedEntities || selectedEntities.length === 0) {
      return null;
    }

    const count = selectedEntities.length;
    const firstEntity = selectedEntities[0];
    const hasVersionOrLabel = firstEntity?.version_number || firstEntity?.label;

    return (
      <>
        <div
          className={`flex flex-shrink-0 items-center justify-center rounded-full p-2 ${getItemColor(
            firstEntity.type,
          )}`}
        >
          <EntityIcon type={firstEntity.type} size={getEntityIconSize(size)} />
        </div>
        {count === 1 ? (
          <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
            <OverflowDisplayTooltip
              content={firstEntity.name}
              className={`truncate font-medium leading-tight ${sizeConfig.text}`}
            >
              {firstEntity.name}
            </OverflowDisplayTooltip>
            {hasVersionOrLabel && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                {firstEntity?.version_number && (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">
                    Version {firstEntity.version_number}
                  </span>
                )}
                {firstEntity?.label && (
                  <LabelBadge label={firstEntity.label} variant="environment" />
                )}
              </div>
            )}
          </div>
        ) : (
          <span className={`flex-1 truncate font-medium ${sizeConfig.text}`}>
            {count} {getEntityName(type)}
            {count > 1 ? "s" : ""} selected
          </span>
        )}
      </>
    );
  };

  const renderEmptyState = () => (
    <>
      <div
        className={`flex flex-shrink-0 items-center justify-center bg-gray-100 ${sizeConfig.icon} ${sizeConfig.iconRounded}`}
      >
        <EntityIcon type={type} disabled size={getEntityIconSize(size)} />
      </div>
      <span className={`truncate text-muted-foreground ${sizeConfig.text}`}>
        {isMultiMode ? `Select ${getEntityName(type)}s` : `Select ${getEntityName(type)}`}
      </span>
    </>
  );

  const renderErrorState = () => {
    if (!selectedEntity) {
      return (
        <>
          <div
            className={`flex flex-shrink-0 items-center justify-center bg-red-100 ${sizeConfig.icon} ${sizeConfig.iconRounded}`}
          >
            <AlertCircleIcon className={`${sizeConfig.icon} text-red-600`} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
            <span className={`truncate font-medium text-red-700 ${sizeConfig.text}`}>
              {errorMessage || `${getEntityName(type)} not found`}
            </span>
            <span className="text-xs text-red-600">Click to select replacement</span>
          </div>
        </>
      );
    }

    const hasVersionOrLabel = selectedEntity?.version_number || selectedEntity?.label;

    return (
      <>
        <div
          className={`flex flex-shrink-0 items-center justify-center bg-red-100 ${sizeConfig.icon} ${sizeConfig.iconRounded}`}
        >
          <AlertCircleIcon className={`${sizeConfig.icon} text-red-600`} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
          <OverflowDisplayTooltip
            content={selectedEntity.name}
            className={`truncate font-medium text-red-700 ${sizeConfig.text}`}
          >
            {selectedEntity.name}
          </OverflowDisplayTooltip>
          {hasVersionOrLabel && (
            <div className="flex items-center gap-1.5 overflow-hidden text-xs">
              {selectedEntity?.version_number && (
                <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                  Version {selectedEntity.version_number}
                </span>
              )}
              {selectedEntity?.label && <LabelBadge label={selectedEntity.label} variant="error" />}
            </div>
          )}
          <span className="truncate text-xs text-red-600">
            {errorMessage || `${getEntityName(type)} not found`} · Click to select replacement
          </span>
        </div>
      </>
    );
  };

  const renderTriggerContent = () => {
    if (isLoading) {
      return <LoadingState />;
    }
    if (hasError) {
      return renderErrorState();
    }
    if (hasSelection) {
      return isMultiMode ? renderMultiSelection() : renderSingleSelection();
    }
    return renderEmptyState();
  };

  const triggerContent = (
    <div
      className={`group relative flex w-full max-w-full items-center overflow-hidden rounded-md border bg-white shadow-sm transition-all ${sizeConfig.container} ${
        hasError
          ? "border-red-300 bg-red-50/30 hover:border-red-400 hover:bg-red-50/50"
          : "border-input"
      } ${
        readonly || isLoading
          ? "cursor-default"
          : "cursor-pointer hover:bg-gray-50 hover:text-accent-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      } ${className}`}
    >
      <div className="flex w-0 min-w-0 flex-1 items-center gap-2.5 overflow-hidden">
        {renderTriggerContent()}
      </div>
      {!isLoading && (
        <div className="relative z-10 ml-2 flex flex-shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {!readonly && onInlineCreate && (
            <BasicTooltip content={`Create new ${getEntityName(type)}`}>
              <Button
                variant="ghost"
                size="tinyIcon"
                className={`${sizeConfig.actionButton} rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                onClick={e => {
                  e.stopPropagation();
                  onInlineCreate();
                }}
              >
                <PlusIcon className={sizeConfig.actionIcon} />
              </Button>
            </BasicTooltip>
          )}
          {!readonly && !hasError && !isMultiMode && onInlineEdit && selectedEntity && (
            <BasicTooltip content={`Edit ${getEntityName(type)}`}>
              <Button
                variant="ghost"
                size="tinyIcon"
                className={`${sizeConfig.actionButton} rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                onClick={e => {
                  e.stopPropagation();
                  onInlineEdit();
                }}
              >
                <PencilIcon className={sizeConfig.actionIcon} />
              </Button>
            </BasicTooltip>
          )}
          {!hasError && url && selectedEntity && !isMultiMode && (
            <BasicTooltip content="Open in new tab">
              <Button
                variant="ghost"
                size="tinyIcon"
                className={`${sizeConfig.actionButton} rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                onClick={e => {
                  e.stopPropagation();
                  window.open(url, "_blank");
                }}
              >
                <OpenInNewWindowIcon className={sizeConfig.actionIcon} />
              </Button>
            </BasicTooltip>
          )}
        </div>
      )}
    </div>
  );

  if (readonly || isLoading) {
    return triggerContent;
  }

  return (
    <>
      <DialogTrigger asChild>{triggerContent}</DialogTrigger>
      {isMultiMode && selectedEntities && selectedEntities.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedEntities.map(e => (
            <MiniEntityItem
              key={e.id}
              type={e.type}
              name={e.name}
              label={e.label}
              version_number={e.version_number}
              onRemove={() => {
                if (onSelect) {
                  const updatedEntities = selectedEntities.filter(entity => entity.id !== e.id);
                  onSelect(updatedEntities);
                }
              }}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default ChooseEntityDialogTrigger;
